import React, { Suspense, useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ENABLED } from '../../utils/adminFeature';
import UserAvatar from '../../components/common/UserAvatar';
import AvatarPickerModal from '../../components/common/AvatarPickerModal';
import OrderHistory from '../account/OrderHistory';
import Victories from '../account/Victories';
import UserInfo from '../account/UserInfo';
import Referrals from '../account/Referrals';
import Notifications from '../account/Notifications';
import RecommendationLetter from '../account/RecommendationLetter';
import RevisionSheets from '../account/RevisionSheets';
import HoursRecord from '../account/HoursRecord';
import HelpSettings from '../account/HelpSettings';
import BlogList from '../blog/BlogList';
import FaqList from '../faq/FaqList';
import About from '../../pages/About';
const AdminDashboard = ADMIN_ENABLED
    ? React.lazy(() => import('../admin/AdminDashboard'))
    : null;
import SchoolStats from '../account/SchoolStats';

// ─── Nav definition ───────────────────────────────────────────────────────────
// Component: rendered inline in right panel
// route: close modal + navigate on click
const SECTIONS = [
    {
        groupFr: 'Mon espace',
        groupEn: 'My space',
        items: [
            {
                id: 'orders',
                labelFr: 'Mes achats',
                labelEn: 'Purchases',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>,
                Component: OrderHistory,
            },
            {
                id: 'victories',
                labelFr: 'Victoires',
                labelEn: 'Victories',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>,
                Component: Victories,
            },
            {
                id: 'infos',
                labelFr: 'Mes infos',
                labelEn: 'My info',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
                Component: UserInfo,
            },
            {
                id: 'parrainages',
                labelFr: 'Mes parrainages',
                labelEn: 'Referrals',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
                Component: Referrals,
            },
            {
                id: 'notifications',
                labelFr: 'Notifications',
                labelEn: 'Notifications',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>,
                Component: Notifications,
            },
            {
                id: 'recommendation',
                labelFr: 'Recommandation',
                labelEn: 'Recommendation',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
                Component: RecommendationLetter,
            },
            {
                id: 'revision-sheets',
                labelFr: 'Fiches de révision',
                labelEn: 'Study sheets',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
                Component: RevisionSheets,
            },
            {
                id: 'hours',
                labelFr: "Relevé d'heures",
                labelEn: 'Hours record',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                Component: HoursRecord,
            },
        ],
    },
    {
        groupFr: 'Aide & ressources',
        groupEn: 'Help & resources',
        items: [
            {
                id: 'aide',
                labelFr: 'Tutoriel',
                labelEn: 'Tutorial',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" /><path d="M4.93 4.93l4.24 4.24" /><path d="M14.83 14.83l4.24 4.24" /><path d="M14.83 9.17l4.24-4.24" /><path d="M4.93 19.07l4.24-4.24" /></svg>,
                Component: HelpSettings,
            },
            {
                id: 'blog',
                labelFr: 'Blogue',
                labelEn: 'Blog',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 12h6m-6-4h4" /></svg>,
                Component: BlogList,
            },
            {
                id: 'faq',
                labelFr: 'Aide',
                labelEn: 'Help',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                Component: FaqList,
            },
            {
                id: 'about',
                labelFr: 'À propos',
                labelEn: 'About',
                icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                Component: About,
            },
        ],
    },
];

// ─── Back arrow button (shared between mobile views) ──────────────────────────
const BackArrow = ({ onClick }) => (
    <button
        onClick={onClick}
        className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/20 transition"
    >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
    </button>
);

// ─── Component ────────────────────────────────────────────────────────────────
const UserAccountModal = ({ onClose, defaultSection = 'infos' }) => {
    const { user, logout, isAdmin } = useAuth();
    const { language, toggleLanguage } = useLanguage();
    const { theme, setTheme } = useTheme();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState(() => {
        // If a specific section was requested (e.g. from URL), use it;
        // otherwise restore last visited section from localStorage
        if (defaultSection !== 'infos') return defaultSection;
        return localStorage.getItem('nizus_account_section') || defaultSection;
    });
    const isSchoolAdmin = (user?.nroEcole ?? 0) > 0;

    // Build sections dynamically — add Administration group if needed
    const adminItems = [];
    if (ADMIN_ENABLED && isAdmin && AdminDashboard) adminItems.push({
        id: 'admin',
        labelFr: 'Admin',
        labelEn: 'Admin',
        icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
        Component: AdminDashboard,
        fullWidth: true,
    });
    if (isSchoolAdmin) adminItems.push({
        id: 'school-stats',
        labelFr: 'Stats étudiants',
        labelEn: 'Students stats',
        icon: <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" /></svg>,
        Component: SchoolStats,
    });

    const allSections = adminItems.length > 0
        ? [...SECTIONS, { groupFr: 'Administration', groupEn: 'Administration', items: adminItems }]
        : SECTIONS;

    const allItems = allSections.flatMap(s => s.items);

    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [mobileView, setMobileView] = useState('menu'); // 'menu' | 'content'
    const [showAvatarPicker, setShowAvatarPicker] = useState(false);

    // Persist last active section + auto-collapse sidebar for fullWidth items
    useEffect(() => {
        localStorage.setItem('nizus_account_section', activeSection);
        const itemDef = allItems.find(item => item.id === activeSection);
        if (itemDef?.fullWidth) {
            setSidebarOpen(false);
        } else {
            setSidebarOpen(true);
        }
    }, [activeSection]);

    const handleLogout = () => {
        logout();
        onClose();
        navigate('/');
    };

    const activeItemDef   = allItems.find(item => item.id === activeSection);
    const ActiveComponent = activeItemDef?.Component;

    return (
        <div className="fixed inset-0 z-[300] bg-white dark:bg-[#111]">

            {/* ── Mobile drill-down layout ─────────────────────────────────── */}
            <div className="md:hidden h-full flex flex-col">
                {mobileView === 'menu' ? (
                    <>
                        {/* Header with close button */}
                        <div className="flex items-center px-5 pt-5 pb-2 flex-shrink-0">
                            <BackArrow onClick={onClose} />
                        </div>

                        {/* Menu items */}
                        <nav data-help="account-nav" className="flex-1 overflow-y-auto px-5 py-6 space-y-1">
                            {allItems.map(item => (
                                <button
                                    key={item.id}
                                    onClick={() => { setActiveSection(item.id); setMobileView('content'); }}
                                    className="w-full flex items-center gap-4 px-4 py-4 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl transition"
                                >
                                    <span className="text-gray-400 dark:text-gray-500">{item.icon}</span>
                                    {language === 'fr' ? item.labelFr : item.labelEn}
                                    {item.id === 'notifications' && (user?.unreadNotifications > 0 || user?.unreadMessages > 0) && (
                                        <span className="w-2.5 h-2.5 bg-red-500 rounded-full ml-auto flex-shrink-0" />
                                    )}
                                </button>
                            ))}
                        </nav>

                        {/* Logout at bottom */}
                        <div className="px-5 py-5 border-t border-gray-100 dark:border-white/5 flex-shrink-0">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-4 px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl transition"
                            >
                                <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                {language === 'fr' ? 'Déconnexion' : 'Sign out'}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Header with back-to-menu button */}
                        <div className="flex items-center px-5 pt-5 pb-2 flex-shrink-0">
                            <BackArrow onClick={() => setMobileView('menu')} />
                        </div>

                        {/* Content */}
                        <div data-help="account-content" className="flex-1 overflow-auto custom-scrollbar px-4 pb-8">
                            {ActiveComponent && (
                                <Suspense fallback={<div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>}>
                                    {activeItemDef?.fullWidth
                                        ? <ActiveComponent onBack={() => setMobileView('menu')} inModal />
                                        : <ActiveComponent />
                                    }
                                </Suspense>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* ── Desktop two-panel layout ─────────────────────────────────── */}
            <div className="hidden md:flex h-full">

                {/* ── Left nav ─────────────────────────────────────────────── */}
                <div className={`${sidebarOpen ? 'w-64' : 'w-0'} transition-[width] duration-300 overflow-hidden bg-gray-50 dark:bg-[#0a0a0a] border-r border-gray-100 dark:border-white/5 flex flex-col flex-shrink-0`}>
                    <div className="w-64 flex flex-col h-full">
                        {/* top padding to clear hamburger button */}
                        <div className="h-16 flex-shrink-0" />

                        {/* Nav sections */}
                        <nav data-help="account-nav" className="flex-1 px-3 pb-4 overflow-y-auto custom-scrollbar space-y-5">
                            {allSections.map(section => (
                                <div key={section.groupFr}>
                                    <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-600">
                                        {language === 'fr' ? section.groupFr : section.groupEn}
                                    </p>
                                    <div className="space-y-0.5">
                                        {section.items.map(item => (
                                            <button
                                                key={item.id}
                                                onClick={() => setActiveSection(item.id)}
                                                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium flex items-center gap-3 transition-all ${
                                                    activeSection === item.id
                                                        ? 'bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm'
                                                        : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
                                                }`}
                                            >
                                                {item.icon}
                                                {language === 'fr' ? item.labelFr : item.labelEn}
                                                {item.id === 'notifications' && (user?.unreadNotifications > 0 || user?.unreadMessages > 0) && (
                                                    <span className="w-2 h-2 bg-red-500 rounded-full ml-auto flex-shrink-0" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </nav>

                        {/* Langue & affichage sub-menu */}
                        <div data-help="account-display-settings" className="px-3 pt-3 border-t border-gray-100 dark:border-white/5">
                            <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-600">
                                {language === 'fr' ? 'Langue & affichage' : 'Language & display'}
                            </p>
                            <div className="px-3 py-1 flex items-center gap-2">
                                <button
                                    onClick={toggleLanguage}
                                    className="px-2.5 py-2 rounded-xl bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/20 hover:text-gray-900 dark:hover:text-white transition-all"
                                    title={language === 'fr' ? 'Switch to English' : 'Passer en français'}
                                >
                                    {language === 'fr' ? 'EN' : 'FR'}
                                </button>
                                <button
                                    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                                    className="p-2 rounded-xl bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/20 hover:text-gray-900 dark:hover:text-white transition-all"
                                    title={theme === 'dark' ? (language === 'fr' ? 'Mode clair' : 'Light mode') : (language === 'fr' ? 'Mode sombre' : 'Dark mode')}
                                >
                                    {theme === 'dark' ? (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Logout + user info at bottom */}
                        <div className="px-3 pt-3 border-t border-gray-100 dark:border-white/5">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all"
                            >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                {language === 'fr' ? 'Déconnexion' : 'Sign out'}
                            </button>
                            <div className="mt-2 px-3 py-2 flex items-center gap-3">
                                <button
                                    onClick={() => setShowAvatarPicker(true)}
                                    className="relative flex-shrink-0 group"
                                    title={language === 'fr' ? "Changer d'avatar" : 'Change avatar'}
                                >
                                    <UserAvatar size="md" />
                                    <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                                        <Pencil className="w-3.5 h-3.5 text-white" />
                                    </div>
                                </button>
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold text-gray-900 dark:text-white text-sm truncate">
                                        {user?.name || user?.email}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                        {user?.email}
                                    </p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* ── Right content ────────────────────────────────────────── */}
                <div data-help="account-content" className="flex-1 overflow-auto custom-scrollbar">
                    {ActiveComponent && (
                        <Suspense fallback={<div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>}>
                            {activeItemDef?.fullWidth
                                ? <ActiveComponent onBack={() => setActiveSection('infos')} inModal />
                                : <div className="px-4 md:px-[10%] py-8"><ActiveComponent /></div>
                            }
                        </Suspense>
                    )}
                </div>

                {/* ── Back arrow — closes the modal (same style as exam/course back) ── */}
                <button
                    onClick={onClose}
                    className="absolute top-4 left-4 z-10 w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-700 dark:text-white flex items-center justify-center transition"
                    title={language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}
                    aria-label="Back"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                </button>

                {/* ── Sidebar toggle — top-right edge of sidebar, facing the back arrow.
                       Slides next to the back arrow when sidebar is collapsed. ── */}
                {!activeItemDef?.fullWidth && (
                    <button
                        onClick={() => setSidebarOpen(v => !v)}
                        className={`absolute top-5 z-10 w-8 h-8 rounded-xl bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 flex flex-col items-center justify-center gap-[4px] hover:bg-gray-200 dark:hover:bg-white/20 transition-all duration-300 ${sidebarOpen ? 'left-56' : 'left-16'}`}
                        title={sidebarOpen ? (language === 'fr' ? 'Masquer le menu' : 'Hide menu') : (language === 'fr' ? 'Afficher le menu' : 'Show menu')}
                        aria-label="Toggle sidebar"
                    >
                        <span className="block w-3.5 h-[1.5px] bg-gray-500 dark:bg-gray-400 rounded-full" />
                        <span className="block w-3.5 h-[1.5px] bg-gray-500 dark:bg-gray-400 rounded-full" />
                        <span className="block w-3.5 h-[1.5px] bg-gray-500 dark:bg-gray-400 rounded-full" />
                    </button>
                )}

            </div>

            {showAvatarPicker && (
                <AvatarPickerModal onClose={() => setShowAvatarPicker(false)} />
            )}
        </div>
    );
};

export default UserAccountModal;
